import Loader from '@components/framework/loader/Loader';
import {State} from '@shared/store/reducers/Index';
import {useSnackbar} from 'notistack';
import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import './PriorAuthPatientDrawerDetails.scss';
import TicketingSystemGrid from '../../TicketingSystem/TicketingSystemGrid/TicketingSystemGrid';
import PriorAuthorizationTicketDetailsFields from '../PriorAuthorizationTicketDetailsFields/PriorAuthorizationTicketDetailsFields';
import {getClinicUsersList} from '@shared/services/ClinicServices';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import {
  MESSAGES_EVENT_TYPE_MAPPER,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {
  IPriorAuthTicketDetails,
  IWorkFlow
} from '@shared/services/service-mappers/WorkflowMapper';
import usePriorAuthTicketFieldsData from '../hooks/usePriorAuthTicketFieldsData';
import {getWorkflowDetails} from '@shared/services/WorkflowServices';

const PriorAuthPatientDrawerDetails = (): JSX.Element => {
  const {groupId, clinicId, patientDetails} = useSelector(
    (state: State) => state.patientDetailsDrawer
  );
  const [priorAuthWorkflow, setPriorAuthWorkflow] = useState({
    isLoading: false,
    data: null as null | IWorkFlow
  });
  useEffect(() => {
    const fetchPriorAuthWorkflow = async () => {
      try {
        setPriorAuthWorkflow(p => ({
          ...p,
          isLoading: true
        }));

        const response = await getWorkflowDetails({
          clinicGroupId: groupId,
          params: {
            module:
              TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[
                'PRIOR_AUTHORIZATION'
              ]
          }
        });
        if (response.data.workflow) {
          setPriorAuthWorkflow({
            isLoading: false,
            data: response.data.workflow
          });
        } else {
          setPriorAuthWorkflow({
            isLoading: false,
            data: null
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setPriorAuthWorkflow({
          isLoading: false,
          data: null
        });
      }
    };
    if (groupId) {
      fetchPriorAuthWorkflow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  const user = useSelector((state: State) => state.auth.user);
  const [commonState, setCommonState] = React.useState({
    isLoading: true,
    allClinicUsers: [] as UserModelMapper[]
  });

  const {enqueueSnackbar} = useSnackbar();

  const [ticketDetailsView, setTicketDetailsView] = useState<{
    show: boolean;
    ticketId?: number | null;
  }>({
    show: false
  });

  const updateTicketDetailsView = ({
    show,
    ticketId
  }: {
    show: boolean;
    ticketId?: number | null;
  }) => {
    setTicketDetailsView({show, ticketId});
  };

  const {
    clinicPayersList,
    serviceLocations,
    clinicProvidersList,
    customFields
  } = usePriorAuthTicketFieldsData({groupId, clinicId});

  useEffect(() => {
    const init = async () => {
      setCommonState(prev => ({
        ...prev,
        isLoading: true
      }));
      try {
        const response = await getClinicUsersList(groupId, clinicId);

        if (response.data) {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: response.data
          }));
        } else {
          setCommonState(prev => ({
            ...prev,
            allClinicUsers: []
          }));
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
      } finally {
        setCommonState(prev => ({
          ...prev,
          isLoading: false
        }));
      }
    };
    if (groupId && clinicId) {
      init();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, groupId]);
  const ticketFilters = useMemo(() => ({}), []);

  if (commonState.isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <div className={`prior-auth-patient-drawer`}>
      {priorAuthWorkflow.isLoading && <Loader />}
      {!priorAuthWorkflow.isLoading && priorAuthWorkflow.data && (
        <TicketingSystemGrid
          module={'PRIOR_AUTHORIZATION'}
          groupPermission={'PRIOR_AUTHORIZATION_ENABLE'}
          clinicGroupId={groupId}
          clinicId={clinicId}
          statuses={priorAuthWorkflow.data.status}
          allClinicUsers={commonState.allClinicUsers}
          workflowId={priorAuthWorkflow.data.id}
          ticketFilters={ticketFilters}
          patientId={patientDetails.patient_id}
          userId={user.id}
          renderTicketDetailsModuleInputFields={({
            isEditing,
            methods,
            ticketDetails,
            refreshTicketDetails,
            isLoadingTicketDetails
          }) => (
            <PriorAuthorizationTicketDetailsFields
              isEditing={isEditing}
              methods={methods}
              clinicGroupId={groupId}
              ticketDetails={ticketDetails as IPriorAuthTicketDetails}
              refreshTicketDetails={refreshTicketDetails}
              clinicPayersList={clinicPayersList}
              serviceLocations={serviceLocations}
              clinicProvidersList={clinicProvidersList}
              customFields={customFields}
              key={isLoadingTicketDetails.toString()}
              workFlow={priorAuthWorkflow.data}
            />
          )}
          showPatientInfoInGridAndTicketDetails={false}
          messageEventType={MESSAGES_EVENT_TYPE_MAPPER.PRIOR_AUTHORIZATION}
          ticketDetailsView={ticketDetailsView}
          updateTicketDetailsView={updateTicketDetailsView}
        />
      )}
    </div>
  );
};

export default PriorAuthPatientDrawerDetails;
